/**
 * HiGHS Linear Programming Library Wrapper
 * Provides types and utilities for constructing and solving linear optimization models
 */

// Core types for building optimization models

export type VariableType = 'continuous' | 'integer' | 'binary';

/**
 * The value of a single index ("column") of a grouped variable, e.g. a
 * generator id (string) or a timestamp (number).
 */
export type IndexValue = string | number;

export interface Variable {
  name: string;
  type?: VariableType;
  lowerBound?: number;
  upperBound?: number;
  /**
   * Name of the logical "table" this variable belongs to, e.g. "P_gen" or
   * "P_line". Variables that share a group have the same set of index columns.
   * Attached upstream when the model is built so the grouping survives all the
   * way to the UI without re-parsing variable name strings.
   */
  group?: string;
  /**
   * Values for this variable's index columns, keyed by column name, e.g.
   * `{ gen_id: "gen1", timestamp: 0 }`. Insertion order defines column order.
   */
  indices?: Record<string, IndexValue>;
}

/** One named cost category's contribution to the objective. */
export interface ObjectiveTermGroup {
  name: string;
  coefficients: Map<string, number>; // variable name -> coefficient
}

export interface ObjectiveFunction {
  sense: 'minimize' | 'maximize';
  coefficients: Map<string, number>; // variable name -> coefficient
  /**
   * The per-category breakdown that `coefficients` is the sum of, when the
   * objective was built from named cost terms. Present so the UI can show each
   * category's own rows; the solver only ever reads `coefficients`.
   */
  terms?: ObjectiveTermGroup[];
}

export type ConstraintSense = '<=' | '>=' | '=';

export interface Constraint {
  name: string;
  coefficients: Map<string, number>; // variable name -> coefficient
  sense: ConstraintSense;
  rhs: number; // right-hand side value
  /**
   * Name of the logical "table" this constraint belongs to, e.g. "dcpf" or
   * "balance". Constraints that share a group have the same set of index
   * columns. Attached upstream when the model is built so the grouping survives
   * to the UI without re-parsing constraint name strings.
   */
  group?: string;
  /**
   * Values for this constraint's index columns, keyed by column name, e.g.
   * `{ edge_id: "edge3", timestamp: 0 }`. Insertion order defines column order.
   */
  indices?: Record<string, IndexValue>;
}

export interface Model {
  variables: Variable[];
  objective: ObjectiveFunction;
  constraints: Constraint[];
}

// HiGHS raw solution structure
export interface HighsRawSolution {
  Status: string;
  ObjectiveValue?: number;
  Columns?: Record<string, { Primal?: number; Dual?: number; Status?: string }>;
  Rows?: Record<string, { Primal?: number; Dual?: number }>;
  error?: string;
}

export interface Solution {
  status: string;
  objectiveValue?: number;
  variables: Map<string, number>; // variable name -> solution value
  dualVariables?: Map<string, number>; // constraint name -> dual value
  raw?: HighsRawSolution; // raw HiGHS solution object
}

// HiGHS configuration for browser and Node.js environments
const highsSettings = {
  locateFile: (file: string) => {
    // Browser main thread (window) or Web Worker (self.importScripts): the WASM
    // is served from the same origin as the page/worker script.
    const isBrowserLike =
      typeof window !== 'undefined' ||
      (typeof self !== 'undefined' && typeof (self as any).importScripts === 'function');
    if (isBrowserLike) {
      return `./${file}`;
    }
    // Node.js environment - use the file from node_modules
    return require('path').join(__dirname, '../../node_modules/highs/build/', file);
  }
};

// A single record of HiGHS ranging output, one entry per row (or column). See
// `getRanging` below. Backed by typed arrays indexed by row/column number.
interface HighsRangingRecord {
  value: Float64Array;
  objective: Float64Array;
  inVariable: Int32Array;
  outVariable: Int32Array;
}

interface HighsRangingResult {
  colCostUp: HighsRangingRecord;
  colCostDown: HighsRangingRecord;
  colBoundUp: HighsRangingRecord;
  colBoundDown: HighsRangingRecord;
  /** How far each row's bound (RHS) can rise before the optimal basis changes. */
  rowBoundUp: HighsRangingRecord;
  /** How far each row's bound (RHS) can fall before the optimal basis changes. */
  rowBoundDown: HighsRangingRecord;
}

// The subset of the persistent-model API (highs >= 1.15.3) that we use. A
// persistent model owns a HiGHS instance across calls, so we can change a single
// row's bound and re-solve (warm-started) instead of rebuilding the whole LP.
interface HighsPersistentModel {
  readModel(source: { format: 'lp' | 'mps'; data: string }): unknown;
  run(): { modelStatus: number };
  getSolution(): {
    colValue: Float64Array;
    rowValue: Float64Array;
    colDual: Float64Array;
    rowDual: Float64Array;
  };
  getRowByName(name: string): number;
  changeRowBounds(row: number, lower: number, upper: number): unknown;
  getRanging(): HighsRangingResult;
  dispose(): void;
}

// HiGHS instance type
interface HighsInstance {
  solve(lpString: string): HighsRawSolution;
  createModel(source?: unknown): HighsPersistentModel;
}

/** HiGHS `HighsModelStatus::kOptimal`. */
const MODEL_STATUS_OPTIMAL = 7;

/**
 * Values at or above this magnitude coming back from HiGHS ranging represent an
 * unbounded direction (HiGHS uses 1e30 for infinity), not a real breakpoint.
 */
export const HIGHS_RANGING_INFINITY = 1e15;

// Lazy-load HiGHS instance
let highsInstance: HighsInstance | null = null;

async function getHighsInstance(): Promise<HighsInstance> {
  if (!highsInstance) {
    const highs = require('highs');
    highsInstance = await highs(highsSettings) as HighsInstance;
  }
  return highsInstance;
}

/**
 * One point on an RHS ranging sweep: the dual (LMP) of the swept constraint at a
 * given right-hand side, together with the interval of right-hand sides over
 * which that dual — i.e. the optimal basis — is unchanged.
 */
export interface RowRangePoint {
  /** Dual value of the swept constraint at this RHS (LMP), always non-negative. */
  lmp: number;
  /** Lowest RHS for which the current basis (and dual) stays optimal. */
  downMW: number;
  /** Highest RHS for which the current basis (and dual) stays optimal. */
  upMW: number;
}

/**
 * A reusable handle for sweeping one equality constraint's RHS. Backed by a
 * persistent HiGHS model: each `evalAt` changes just that row's bound and
 * re-solves (warm-started), then reads back the dual and the RHS ranging in a
 * single HiGHS `getRanging` call. Callers must `dispose()` when done.
 */
export interface RangingSweeper {
  /** Solve with the swept row's RHS set to `rhs`; null if infeasible. */
  evalAt(rhs: number): RowRangePoint | null;
  dispose(): void;
}

/**
 * Build a {@link RangingSweeper} over the constraint named `rowName`. Loads the
 * model into a persistent HiGHS instance once; subsequent `evalAt` calls only
 * mutate the one row's bound. Throws if the constraint is not in the model.
 */
export async function createRangingSweeper(
  model: Model,
  rowName: string
): Promise<RangingSweeper> {
  const highs = await getHighsInstance();
  const lpString = modelToLPFormat(model);
  const hm = highs.createModel();
  hm.readModel({ format: 'lp', data: lpString });

  let rowIdx = -1;
  try {
    rowIdx = hm.getRowByName(rowName);
  } catch {
    rowIdx = -1;
  }
  if (rowIdx < 0) {
    hm.dispose();
    throw new Error(`Ranging sweep: constraint "${rowName}" not found in model`);
  }

  return {
    evalAt(rhs: number): RowRangePoint | null {
      // Equality constraint: keep lower == upper == rhs.
      hm.changeRowBounds(rowIdx, rhs, rhs);
      const run = hm.run();
      if (run.modelStatus !== MODEL_STATUS_OPTIMAL) {
        return null;
      }
      const solution = hm.getSolution();
      const ranging = hm.getRanging();
      // For a minimization with an equality balance constraint the dual is the
      // marginal cost (LMP); take the magnitude to match the display convention.
      const lmp = Math.abs(solution.rowDual[rowIdx] ?? 0);
      return {
        lmp,
        downMW: ranging.rowBoundDown.value[rowIdx],
        upMW: ranging.rowBoundUp.value[rowIdx],
      };
    },
    dispose() {
      hm.dispose();
    },
  };
}

/**
 * Serialize a Model to HiGHS LP format string
 */
export function modelToLPFormat(model: Model): string {
  const lines: string[] = [];
  
  // Objective function
  const objSense = model.objective.sense === 'minimize' ? 'Minimize' : 'Maximize';
  lines.push(objSense);
  
  const objTerms: string[] = [];
  model.objective.coefficients.forEach((coeff, varName) => {
    if (coeff !== 0) {
      if (coeff === 1) {
        objTerms.push(varName);
      } else if (coeff === -1) {
        objTerms.push(`-${varName}`);
      } else {
        objTerms.push(`${coeff} ${varName}`);
      }
    }
  });
  
  lines.push(`  obj: ${objTerms.join(' + ').replace(/\+ -/g, '- ')}`);
  lines.push('');
  
  // Constraints
  if (model.constraints.length > 0) {
    lines.push('Subject To');
    
    model.constraints.forEach(constraint => {
      const terms: string[] = [];
      constraint.coefficients.forEach((coeff, varName) => {
        if (coeff !== 0) {
          if (coeff === 1) {
            terms.push(varName);
          } else if (coeff === -1) {
            terms.push(`-${varName}`);
          } else {
            terms.push(`${coeff} ${varName}`);
          }
        }
      });
      
      const lhs = terms.join(' + ').replace(/\+ -/g, '- ');
      lines.push(`  ${constraint.name}: ${lhs} ${constraint.sense} ${constraint.rhs}`);
    });
    lines.push('');
  }
  
  // Variable bounds
  const boundsLines: string[] = [];
  const integerVars: string[] = [];
  const binaryVars: string[] = [];
  
  model.variables.forEach(variable => {
    const lower = variable.lowerBound ?? -Infinity;
    const upper = variable.upperBound ?? Infinity;
    
    if (variable.type === 'binary') {
      binaryVars.push(variable.name);
      // Binary variables have implicit bounds 0 <= x <= 1
      if (lower !== 0 || upper !== 1) {
        // Override with explicit bounds if different
        if (isFinite(lower) && isFinite(upper)) {
          boundsLines.push(`  ${lower} <= ${variable.name} <= ${upper}`);
        } else if (isFinite(lower)) {
          boundsLines.push(`  ${lower} <= ${variable.name}`);
        } else if (isFinite(upper)) {
          boundsLines.push(`  ${variable.name} <= ${upper}`);
        }
      }
    } else if (variable.type === 'integer') {
      integerVars.push(variable.name);
      // Add bounds for integer variables
      if (isFinite(lower) && isFinite(upper)) {
        boundsLines.push(`  ${lower} <= ${variable.name} <= ${upper}`);
      } else if (isFinite(lower)) {
        boundsLines.push(`  ${lower} <= ${variable.name}`);
      } else if (isFinite(upper)) {
        boundsLines.push(`  ${variable.name} <= ${upper}`);
      }
    } else {
      // Continuous variables
      if (isFinite(lower) && isFinite(upper)) {
        boundsLines.push(`  ${lower} <= ${variable.name} <= ${upper}`);
      } else if (isFinite(lower)) {
        boundsLines.push(`  ${lower} <= ${variable.name}`);
      } else if (isFinite(upper)) {
        boundsLines.push(`  ${variable.name} <= ${upper}`);
      }
    }
  });
  
  if (boundsLines.length > 0) {
    lines.push('Bounds');
    lines.push(...boundsLines);
    lines.push('');
  }
  
  // Integer variables
  if (integerVars.length > 0) {
    lines.push('General');
    lines.push(`  ${integerVars.join(' ')}`);
    lines.push('');
  }
  
  // Binary variables
  if (binaryVars.length > 0) {
    lines.push('Binary');
    lines.push(`  ${binaryVars.join(' ')}`);
    lines.push('');
  }
  
  lines.push('End');
  
  return lines.join('\n');
}

/**
 * Solve a linear optimization model using HiGHS
 */
export async function solve(model: Model): Promise<Solution> {
  try {
    const highs = await getHighsInstance();
    const lpString = modelToLPFormat(model);

    // Solve the LP problem
    const rawSolution = highs.solve(lpString);

    // Parse the solution
    const solution: Solution = {
      status: rawSolution.Status,
      variables: new Map(),
      raw: rawSolution
    };

    if (rawSolution.Status === 'Optimal') {
      solution.objectiveValue = rawSolution.ObjectiveValue;

      // Extract variable values
      solution.variables = new Map();
      if (rawSolution.Columns) {
        Object.entries(rawSolution.Columns).forEach(([varName, varData]) => {
          solution.variables!.set(varName, varData.Primal || 0);
        });
      }

      // Extract dual values (shadow prices) and map them to constraint names
      solution.dualVariables = new Map();
      if (rawSolution.Rows) {
        // rawSolution.Rows can be either an array or an object
        if (Array.isArray(rawSolution.Rows)) {
          // If it's an array, map indices to constraint names from the model
          rawSolution.Rows.forEach((rowData: any, index: number) => {
            const constraint = model.constraints[index];
            if (constraint && rowData.Dual !== undefined) {
              solution.dualVariables!.set(constraint.name, rowData.Dual);
            }
          });
        } else {
          // If it's an object, use the keys directly
          Object.entries(rawSolution.Rows).forEach(([constraintName, constraintData]) => {
            solution.dualVariables!.set(constraintName, (constraintData as any).Dual || 0);
          });
        }
      }
    }

    return solution;
  } catch (error) {
    return {
      status: 'Error',
      variables: new Map(),
      raw: { 
        Status: 'Error',
        error: error instanceof Error ? error.message : String(error) 
      }
    };
  }
}

/**
 * Convenience builder class for constructing models
 */
export class ModelBuilder {
  private model: Model;
  
  constructor() {
    this.model = {
      variables: [],
      objective: {
        sense: 'minimize',
        coefficients: new Map()
      },
      constraints: []
    };
  }
  
  addVariable(variable: Variable): this {
    this.model.variables.push(variable);
    return this;
  }
  
  addVariables(variables: Variable[]): this {
    this.model.variables.push(...variables);
    return this;
  }
  
  setObjective(sense: 'minimize' | 'maximize', coefficients: Map<string, number>): this {
    this.model.objective = { sense, coefficients };
    return this;
  }
  
  addConstraint(constraint: Constraint): this {
    this.model.constraints.push(constraint);
    return this;
  }
  
  addConstraints(constraints: Constraint[]): this {
    this.model.constraints.push(...constraints);
    return this;
  }
  
  getModel(): Model {
    return this.model;
  }
  
  async solve(): Promise<Solution> {
    return solve(this.model);
  }
}